import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  DollarSign,
  Package,
  Search,
  ScanLine,
  Home,
  RefreshCw,
  CheckCircle2,
  XCircle,
  LogOut,
  Settings,
  Boxes,
  Mail,
  Download,
} from "lucide-react";
import { adminApi, isAdminLoggedIn, logoutAdmin, verifyAdminToken } from "@/utils/auth";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [registrants, setRegistrants] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, unpaid: 0, collected: 0, total_amount_due: 0, total_amount_paid: 0, total_balance: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!isAdminLoggedIn()) {
        navigate("/admin/login");
        return;
      }
      const valid = await verifyAdminToken();
      if (!valid) {
        navigate("/admin/login");
        return;
      }
      fetchData(activeTab);
    };
    checkAuth();
  }, []);

  const fetchData = async (paymentFilter = null) => {
    setLoading(true);
    try {
      const params = {};
      if (paymentFilter && paymentFilter !== "all") {
        params.payment_status = paymentFilter;
      }
      if (search) {
        params.search = search;
      }
      
      const [regRes, statsRes] = await Promise.all([
        adminApi.get("/registrants", { params }),
        adminApi.get("/stats"),
      ]);
      setRegistrants(regRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      toast.loading("Preparing export...");
      const response = await adminApi.get("/registrants/export/csv", {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registrants_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success("Export downloaded!");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to export data");
    }
  };

  useEffect(() => {
    if (isAdminLoggedIn()) {
      fetchData(activeTab);
    }
  }, [activeTab]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(activeTab);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const handleLogout = () => {
    logoutAdmin();
    toast.success("Logged out");
    navigate("/admin/login");
  };

  const togglePayment = async (id, currentStatus, e) => {
    e.stopPropagation();
    const newStatus = currentStatus === "paid" ? "unpaid" : "paid";
    try {
      await adminApi.patch(`/registrants/${id}/payment`, {
        payment_status: newStatus,
      });
      toast.success(`Payment marked as ${newStatus}`);
      fetchData(activeTab);
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const statCards = [
    {
      title: "Total Registered",
      value: stats.total,
      icon: Users,
      color: "text-primary",
      bgColor: "bg-primary/10",
      glowColor: "glow-cyan",
    },
    {
      title: "Total Due",
      value: `$${stats.total_amount_due?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: "text-white",
      bgColor: "bg-white/10",
      glowColor: "",
    },
    {
      title: "Total Paid",
      value: `$${stats.total_amount_paid?.toLocaleString() || 0}`,
      icon: CheckCircle2,
      color: "text-[#CCFF00]",
      bgColor: "bg-[#CCFF00]/10",
      glowColor: "glow-lime",
    },
    {
      title: "Balance Owed",
      value: `$${stats.total_balance?.toLocaleString() || 0}`,
      icon: XCircle,
      color: "text-[#FF0055]",
      bgColor: "bg-[#FF0055]/10",
      glowColor: "",
    },
    {
      title: "Collected",
      value: stats.collected,
      icon: Package,
      color: "text-secondary",
      bgColor: "bg-secondary/10",
      glowColor: "glow-pink",
    },
  ];

  const RegistrantTable = ({ data, showPaymentColumn = true }) => (
    <Table>
      <TableHeader>
        <TableRow className="border-white/10 hover:bg-transparent">
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">#</TableHead>
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">Name</TableHead>
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs hidden md:table-cell">Contact</TableHead>
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">Package</TableHead>
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">Size</TableHead>
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">Due</TableHead>
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">Paid</TableHead>
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">Balance</TableHead>
          {showPaymentColumn && (
            <TableHead className="text-muted-foreground uppercase tracking-wider text-xs">Status</TableHead>
          )}
          <TableHead className="text-muted-foreground uppercase tracking-wider text-xs text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={showPaymentColumn ? 10 : 9} className="text-center py-8 text-muted-foreground">
              {loading ? "Loading..." : "No registrants found"}
            </TableCell>
          </TableRow>
        ) : (
          data.map((reg, index) => {
            const balance = (reg.amount_due || 0) - (reg.amount_paid || 0);
            return (
            <TableRow
              key={reg.id}
              className="border-white/5 table-row-hover cursor-pointer"
              onClick={() => navigate(`/admin/registrant/${reg.id}`)}
              data-testid={`registrant-row-${index}`}
            >
              <TableCell className="font-mono text-primary font-bold">
                #{String(reg.reg_number).padStart(3, "0")}
              </TableCell>
              <TableCell className="font-medium text-white">{reg.name}</TableCell>
              <TableCell className="hidden md:table-cell text-muted-foreground">{reg.contact}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{reg.package}</TableCell>
              <TableCell className="text-white font-medium">{reg.size}</TableCell>
              <TableCell className="text-white font-medium">${reg.amount_due || 0}</TableCell>
              <TableCell className="text-[#CCFF00] font-medium">${reg.amount_paid || 0}</TableCell>
              <TableCell className={`font-medium ${balance > 0 ? 'text-[#FF0055]' : 'text-[#CCFF00]'}`}>
                ${balance}
              </TableCell>
              {showPaymentColumn && (
                <TableCell>
                  <Badge
                    className={`${
                      reg.payment_status === "paid" ? "status-paid" : "status-unpaid"
                    } uppercase text-xs`}
                    data-testid={`payment-badge-${index}`}
                  >
                    {reg.payment_status}
                  </Badge>
                </TableCell>
              )}
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => togglePayment(reg.id, reg.payment_status, e)}
                  className={`${
                    reg.payment_status === "paid"
                      ? "hover:bg-[#FF0055]/20 text-[#FF0055]"
                      : "hover:bg-[#CCFF00]/20 text-[#CCFF00]"
                  }`}
                  data-testid={`toggle-payment-${index}`}
                >
                  {reg.payment_status === "paid" ? "Mark Unpaid" : "Mark Paid"}
                </Button>
              </TableCell>
            </TableRow>
          )})
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-white" data-testid="admin-title">
              BAND COMMAND
            </h1>
            <p className="text-muted-foreground">Registration Dashboard</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/">
              <Button variant="outline" className="border-white/10" data-testid="home-btn">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </Link>
            <Link to="/admin/inventory">
              <Button className="bg-primary hover:bg-primary/90 text-black" data-testid="inventory-btn">
                <Boxes className="w-4 h-4 mr-2" />
                Inventory
              </Button>
            </Link>
            <Link to="/admin/broadcast">
              <Button className="bg-[#CCFF00] hover:bg-[#CCFF00]/90 text-black" data-testid="broadcast-btn">
                <Mail className="w-4 h-4 mr-2" />
                Broadcast
              </Button>
            </Link>
            <Link to="/admin/scanner">
              <Button className="bg-secondary hover:bg-secondary/90" data-testid="scanner-btn">
                <ScanLine className="w-4 h-4 mr-2" />
                Scanner
              </Button>
            </Link>
            <Button 
              onClick={exportCSV}
              className="bg-green-600 hover:bg-green-700 text-white" 
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Link to="/admin/settings">
              <Button variant="outline" className="border-white/10" data-testid="settings-btn">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </Link>
            <Button 
              variant="outline" 
              className="border-white/10 text-red-400 hover:text-red-300"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                className={`glass border-white/10 card-hover ${stat.glowColor}`}
                data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                  </div>
                  <p className={`text-3xl md:text-4xl font-black ${stat.color}`}>
                    {stat.value}
                  </p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
                    {stat.title}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Search */}
        <Card className="glass border-white/10 mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="search-input"
                  placeholder="Search by name, email, or contact..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-muted/50 border-transparent h-10"
                />
              </div>
              <Button type="submit" variant="outline" className="border-white/10" data-testid="search-btn">
                Search
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-white/10"
                onClick={() => fetchData(activeTab)}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Tabs for Payment Status */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 bg-muted/30 p-1 h-auto">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-primary data-[state=active]:text-black py-3 text-sm font-bold uppercase tracking-wider"
              data-testid="tab-all"
            >
              All ({stats.total})
            </TabsTrigger>
            <TabsTrigger 
              value="unpaid" 
              className="data-[state=active]:bg-[#FF0055] data-[state=active]:text-white py-3 text-sm font-bold uppercase tracking-wider"
              data-testid="tab-unpaid"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Unpaid ({stats.unpaid})
            </TabsTrigger>
            <TabsTrigger 
              value="paid" 
              className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black py-3 text-sm font-bold uppercase tracking-wider"
              data-testid="tab-paid"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Paid ({stats.paid})
            </TabsTrigger>
          </TabsList>

          <Card className="glass border-white/10 overflow-hidden">
            <TabsContent value="all" className="m-0">
              <div className="overflow-x-auto">
                <RegistrantTable data={registrants} showPaymentColumn={true} />
              </div>
            </TabsContent>
            
            <TabsContent value="unpaid" className="m-0">
              <div className="p-4 bg-[#FF0055]/10 border-b border-[#FF0055]/30">
                <p className="text-[#FF0055] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Showing {registrants.length} unpaid registrant{registrants.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="overflow-x-auto">
                <RegistrantTable data={registrants} showPaymentColumn={false} />
              </div>
            </TabsContent>
            
            <TabsContent value="paid" className="m-0">
              <div className="p-4 bg-[#CCFF00]/10 border-b border-[#CCFF00]/30">
                <p className="text-[#CCFF00] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Showing {registrants.length} paid registrant{registrants.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="overflow-x-auto">
                <RegistrantTable data={registrants} showPaymentColumn={false} />
              </div>
            </TabsContent>
          </Card>
        </Tabs>
      </div>
    </div>
  );
}
